/**
 * Race rating scale shared by the midterms map fills and the chamber-control
 * card's seat bar. The bucket KEYS are pinned here (they're `?bucket=` URL
 * values and drive labels and segment layout); the cut points and fill colors
 * come from product-metadata via ./midtermsMaps.ts, which validates the remote
 * payload against this key list. Client-safe on purpose (no server imports):
 * the card recomputes everything in the browser as WS quotes arrive.
 *
 * Buckets are keyed by the Democratic win probability; the first bucket whose
 * `min` the probability meets wins. The remote fills are theme-invariant by
 * design — data colors must not shift with the palette or the bar would
 * disagree with the map.
 */

/** All bucket keys in bar order (Dem → Rep); also the `?bucket=` URL values. */
export const PARTY_BUCKETS = ['safe-d', 'likely-d', 'lean-d', 'tossup', 'lean-r', 'likely-r', 'safe-r'] as const;

export type PartyBucket = (typeof PARTY_BUCKETS)[number];

export interface DemWinBucket {
    /** Lowest Democratic win probability (0–1) that lands in this bucket. */
    min: number;
    key: PartyBucket;
    /** Theme-invariant hex fill. */
    fill: string;
}

export function bucketForDemProbability(buckets: readonly DemWinBucket[], demWinProbability: number): PartyBucket {
    const bucket = buckets.find((b) => demWinProbability >= b.min);
    return (bucket ?? buckets[buckets.length - 1]!).key;
}

export function fillForBucket(buckets: readonly DemWinBucket[], bucket: PartyBucket): string {
    return buckets.find((b) => b.key === bucket)?.fill ?? '';
}

/**
 * Party display colors for the forecast cards: the map's theme-invariant data
 * hexes (a party's Safe fill), not `--blue-x10`/`--red-x10` theme vars — those
 * shift in dark and accessible palettes and would diverge from the map.
 */
export function partyTextColors(buckets: readonly DemWinBucket[]): { dem: string; rep: string } {
    return {
        dem: fillForBucket(buckets, 'safe-d'),
        rep: fillForBucket(buckets, 'safe-r'),
    };
}
